import type Application from "../../types/application";
import CompanyLogo from "@components/company_logo";
import { Body, SecondaryBody, SectionHeader, SectionTitle } from "@sprout/typography";
import PreviewIcon from "@sprout/icons/preview";
import EllipsisIcon from "@sprout/icons/ellipsis";
import QuestionIcon from "@sprout/icons/question";
import CheckMarkCircleIcon from "@sprout/icons/check_mark_circle";
import { Row, Stack } from "@sprout/layout";
import useTranslation from "@hooks/use_translation";
import { Tag } from "@sprout/tag.tsx";
import theme from "@/stylesheets/theme.module.scss";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@components/ui/hover_card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu.tsx";
import {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@components/ui/dialog";
import DreamJobModal from "./dream_job_modal";
import { daysUntilNextMonth } from "@utils/date_utils";
import { useState } from "react";
import { router } from "@inertiajs/react";
import useCsrfToken from "@/hooks/use_csrf";
import Button from "@/sprout/button";
import { queryClient } from "@/queries/query_client";

type Props = {
  application: Application;
  hasTokens: boolean;
  onAddDreamJob: () => void;
};

const ApplicationHeader = ({ application, hasTokens, onAddDreamJob }: Props) => {
  const [dreamJobDropdownOpen, setDreamJobDropdownOpen] = useState(false);
  const [isInactiveModalOpen, setIsInactiveModalOpen] = useState(false);
  const { t } = useTranslation("applications");
  const csrfToken = useCsrfToken();

  const dreamJobDescription = () => {
    if (application.duplicate || application.inactive) {
      return t("dream_job.duplicate");
    } else if (application.dream_job) {
      return t("dream_job.already_set");
    } else if (!hasTokens) {
      return t("dream_job.no_tokens", { count: daysUntilNextMonth() });
    } else {
      return t("dream_job.description");
    }
  };

  const handleMarkInactiveConfirm = () => {
    router.patch(
      `/applications/${application.id}/mark_inactive`,
      {
        authenticity_token: csrfToken,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["applications"] });
          router.visit("/applications");
        },
      },
    );
  };

  return (
    <Row gap="24px" align="flex-start">
      <Row gap="24px" align="center">
        <CompanyLogo
          logoUrl={application.company_logo_url}
          companyName={application.company_name}
          size="large"
        />

        <Stack gap="4px">
          {application.dream_job && <Tag label={t("dream_job_tag")} color={theme.marigoldE50} />}
          <SectionHeader>{application.job_title}</SectionHeader>
          <div className="flex items-center gap-2">
            <SectionTitle>{application.company_name}</SectionTitle>
            {application.hasStatus ? (
              <HoverCard>
                <HoverCardTrigger asChild>
                  <div className="flex items-center gap-2 cursor-pointer">
                    <CheckMarkCircleIcon size="sm" viewBox="0 0 16 16" color="green" />
                    <Body>{t("sharing_updates")}</Body>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent>
                  {t("sharing_updates_tooltip", { company_name: application.company_name })}
                </HoverCardContent>
              </HoverCard>
            ) : (
              <HoverCard>
                <HoverCardTrigger asChild>
                  <div className="flex items-center gap-2 cursor-pointer">
                    <QuestionIcon color="gray-800" size="sm" />
                    <Body className="!text-gray-800">{t("not_sharing_updates")}</Body>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent>
                  {t("not_sharing_updates_tooltip", { company_name: application.company_name })}
                </HoverCardContent>
              </HoverCard>
            )}
          </div>
        </Stack>
      </Row>
      <Dialog>
        <DropdownMenu onOpenChange={setDreamJobDropdownOpen}>
          <DropdownMenuTrigger>
            <div
              className={`hover:bg-[#d6e6ff] rounded-full ${dreamJobDropdownOpen && "bg-[#eff1f0]"}`}
            >
              <EllipsisIcon />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-74" align="end">
            <DropdownMenuItem
              onSelect={() => {
                window.open(application.job_post_url, "_blank");
              }}
            >
              <Row align="center" gap={"12px"}>
                <Body>{t("dream_job.open_job_post")}</Body>
                <PreviewIcon />
              </Row>
            </DropdownMenuItem>
            <DialogTrigger
              disabled={
                !hasTokens || application.dream_job || application.inactive || application.duplicate
              }
            >
              <DropdownMenuItem>
                <Row gap={"8px"}>
                  <Stack>
                    <Body>{t("dream_job.set")}</Body>
                    <div className="dream-job-subtext">
                      <SecondaryBody>{dreamJobDescription()}</SecondaryBody>
                    </div>
                  </Stack>
                </Row>
              </DropdownMenuItem>
            </DialogTrigger>
            {!application.inactive && (
              <DropdownMenuItem
                onSelect={() => {
                  setIsInactiveModalOpen(true);
                }}
              >
                <Body>{t("mark_as_inactive")}</Body>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <DreamJobModal application={application} afterSuccess={onAddDreamJob} />
      </Dialog>

      {!application.inactive && (
        <Dialog open={isInactiveModalOpen} onOpenChange={setIsInactiveModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                <SectionTitle>{t("inactive_modal_title")}</SectionTitle>
              </DialogTitle>
            </DialogHeader>
            <DialogDescription>
              <Body>{t("inactive_modal_body")}</Body>
            </DialogDescription>
            <DialogFooter>
              <DialogClose asChild>
                <Button>{t("inactive_modal_cancel")}</Button>
              </DialogClose>
              <Button onClick={handleMarkInactiveConfirm} error>
                {t("inactive_modal_confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Row>
  );
};

export default ApplicationHeader;
